var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __param = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
import { StreamStorageRegistryABI } from '@streamr/network-contracts';
import { TheGraphClient, collect, toEthereumAddress, toStreamID } from '@streamr/utils';
import { Interface } from 'ethers';
import min from 'lodash/min';
import { Lifecycle, inject, scoped } from 'tsyringe';
import { Identity, IdentityInjectionToken } from '../identity/Identity';
import { ConfigInjectionToken } from '../ConfigTypes';
import { RpcProviderSource } from '../RpcProviderSource';
import { StreamIDBuilder } from '../StreamIDBuilder';
import { parseMetadata } from '../StreamMetadata';
import { getEthersOverrides } from '../ethereumUtils';
import { StreamrClientEventEmitter } from '../events';
import { LoggerFactory } from '../utils/LoggerFactory';
import { createCacheMap } from '../utils/Mapping';
import { ChainEventPoller } from './ChainEventPoller';
import { ContractFactory } from './ContractFactory';
import { initContractEventGateway, waitForTx } from './contract';
const GET_ALL_STORAGE_NODES = Symbol('GET_ALL_STORAGE_NODES');
/**
 * Stores storage node assignments (mapping of streamIds <-> storage nodes addresses)
 */
let StreamStorageRegistry = class StreamStorageRegistry {
    streamStorageRegistryContract;
    streamStorageRegistryContractReadonly;
    streamIdBuilder;
    contractFactory;
    rpcProviderSource;
    theGraphClient;
    config;
    identity;
    logger;
    storageNodesCache;
    constructor(streamIdBuilder, contractFactory, rpcProviderSource, chainEventPoller, theGraphClient, config, identity, eventEmitter, loggerFactory) {
        this.streamIdBuilder = streamIdBuilder;
        this.contractFactory = contractFactory;
        this.rpcProviderSource = rpcProviderSource;
        this.theGraphClient = theGraphClient;
        this.config = config;
        this.identity = identity;
        this.logger = loggerFactory.createLogger('StreamStorageRegistry');
        this.streamStorageRegistryContractReadonly = this.contractFactory.createReadContract(toEthereumAddress(this.config.contracts.streamStorageRegistryChainAddress), StreamStorageRegistryABI, rpcProviderSource.getProvider(), 'streamStorageRegistry');
        this.initStreamAssignmentEventListeners(eventEmitter, chainEventPoller, loggerFactory);
        this.storageNodesCache = createCacheMap({
            valueFactory: (query) => {
                return this.getStorageNodes_nonCached(query);
            },
            ...config.cache
        });
    }
    initStreamAssignmentEventListeners(eventEmitter, chainEventPoller, loggerFactory) {
        const transformation = ([streamId, nodeAddress], blockNumber) => ({
            streamId: toStreamID(streamId),
            nodeAddress: toEthereumAddress(nodeAddress),
            blockNumber
        });
        const contractAddress = toEthereumAddress(this.config.contracts.streamStorageRegistryChainAddress);
        const contractInterface = new Interface(StreamStorageRegistryABI);
        initContractEventGateway({
            sourceDefinition: {
                contractInterfaceFragment: contractInterface.getEvent('Added'),
                contractAddress
            },
            sourceEmitter: chainEventPoller,
            targetName: 'streamAddedToStorageNode',
            targetEmitter: eventEmitter,
            transformation,
            loggerFactory
        });
        initContractEventGateway({
            sourceDefinition: {
                contractInterfaceFragment: contractInterface.getEvent('Removed'),
                contractAddress
            },
            sourceEmitter: chainEventPoller,
            targetName: 'streamRemovedFromStorageNode',
            targetEmitter: eventEmitter,
            transformation,
            loggerFactory
        });
    }
    async connectToContract() {
        if (!this.streamStorageRegistryContract) {
            const chainSigner = await this.identity.getTransactionSigner(this.rpcProviderSource);
            this.streamStorageRegistryContract = this.contractFactory.createWriteContract(toEthereumAddress(this.config.contracts.streamStorageRegistryChainAddress), StreamStorageRegistryABI, chainSigner, 'streamStorageRegistry');
        }
    }
    async addStreamToStorageNode(streamIdOrPath, nodeAddress) {
        const streamId = await this.streamIdBuilder.toStreamID(streamIdOrPath);
        this.logger.debug('Add stream to storage node', { streamId, nodeAddress });
        await this.connectToContract();
        const ethersOverrides = await getEthersOverrides(this.rpcProviderSource, this.config);
        await waitForTx(this.streamStorageRegistryContract.addStorageNode(streamId, nodeAddress, ethersOverrides));
        this.storageNodesCache.invalidate((key) => key === streamId);
    }
    async removeStreamFromStorageNode(streamIdOrPath, nodeAddress) {
        const streamId = await this.streamIdBuilder.toStreamID(streamIdOrPath);
        this.logger.debug('Remove stream from storage node', { streamId, nodeAddress });
        await this.connectToContract();
        const ethersOverrides = await getEthersOverrides(this.rpcProviderSource, this.config);
        await waitForTx(this.streamStorageRegistryContract.removeStorageNode(streamId, nodeAddress, ethersOverrides));
        this.storageNodesCache.invalidate((key) => key === streamId);
    }
    async isStoredStream(streamIdOrPath, nodeAddress) {
        const streamId = await this.streamIdBuilder.toStreamID(streamIdOrPath);
        this.logger.debug('Check if stream is stored in storage node', { streamId, nodeAddress });
        return await this.streamStorageRegistryContractReadonly.isStorageNodeOf(streamId, nodeAddress);
    }
    async getStoredStreams(nodeAddress) {
        this.logger.debug('Get stored streams of storage node', { nodeAddress });
        const blockNumbers = [];
        const res = await collect(this.theGraphClient.queryEntities((lastId, pageSize) => {
            const query = `{
                    node (id: "${nodeAddress}") {
                        id
                        metadata
                        lastSeen
                        storedStreams (first: ${pageSize} orderBy: "id" where: { id_gt: "${lastId}"}) {
                            id,
                            metadata
                        }
                    }
                    _meta {
                        block {
                            number
                        }
                    }
                }`;
            return { query };
        }, (response) => {
            // eslint-disable-next-line no-underscore-dangle
            blockNumbers.push(response._meta.block.number);
            return (response.node !== null) ? response.node.storedStreams : [];
        }));
        const streams = res.map((stream) => {
            return { id: toStreamID(stream.id), metadata: parseMetadata(stream.metadata) }; // toStreamID() not strictly necessary
        });
        return {
            streams,
            blockNumber: min(blockNumbers)
        };
    }
    async getStorageNodes(streamIdOrPath) {
        const query = (streamIdOrPath !== undefined) ? await this.streamIdBuilder.toStreamID(streamIdOrPath) : GET_ALL_STORAGE_NODES;
        return this.storageNodesCache.get(query);
    }
    async getStorageNodes_nonCached(query) {
        let queryResults;
        if (query !== GET_ALL_STORAGE_NODES) {
            const streamId = query;
            this.logger.debug('Get storage nodes of stream', { streamId });
            queryResults = await collect(this.theGraphClient.queryEntities((lastId, pageSize) => {
                const query = `{
                        stream (id: "${streamId}") {
                            id
                            metadata
                            storageNodes (first: ${pageSize} orderBy: "id" where: { id_gt: "${lastId}"}) {
                                id
                                metadata
                                lastSeen
                            }
                        }
                    }`;
                return { query };
            }, (response) => {
                return (response.stream !== null) ? response.stream.storageNodes : [];
            }));
        }
        else {
            this.logger.debug('Get all storage nodes');
            queryResults = await collect(this.theGraphClient.queryEntities((lastId, pageSize) => {
                const query = `{
                        nodes (first: ${pageSize} orderBy: "id" where: { id_gt: "${lastId}"}) {
                            id
                            metadata
                            lastSeen
                        }
                    }`;
                return { query };
            }));
        }
        return queryResults.map((node) => toEthereumAddress(node.id));
    }
};
StreamStorageRegistry = __decorate([
    scoped(Lifecycle.ContainerScoped),
    __param(5, inject(ConfigInjectionToken)),
    __param(6, inject(IdentityInjectionToken)),
    __metadata("design:paramtypes", [StreamIDBuilder,
        ContractFactory,
        RpcProviderSource,
        ChainEventPoller,
        TheGraphClient, Object, Identity,
        StreamrClientEventEmitter,
        LoggerFactory])
], StreamStorageRegistry);
export { StreamStorageRegistry };
